import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, Search, Award, Briefcase, CheckCircle, Heart, Bookmark } from "lucide-react";
import { Separator } from "@/components/ui/separator";

interface CandidateProfile {
  id: string;
  candidate_number: string;
  headline: string;
  skills: string[];
  certifications: string[];
  work_experience: any[];
  summary: string;
  ready_to_work: boolean;
  endorsements: string[];
  industry_interests: string[];
  availability: string;
}

export default function FindTalent() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedAvailability, setSelectedAvailability] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<CandidateProfile[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateProfile | null>(null);
  const [savedCandidates, setSavedCandidates] = useState<string[]>([]);

  const industries = ["Hospitality", "Construction", "Retail", "Admin", "Healthcare", "Manufacturing", "Logistics"];
  const availabilityOptions = ["Full-time", "Part-time", "Immediate Start"];

  useEffect(() => {
    // Mock candidate data - In production, this would come from Supabase
    const mockCandidates: CandidateProfile[] = [
      {
        id: "1",
        candidate_number: "11732",
        headline: "Hard-working and certified forklift operator with 3 years of warehouse experience.",
        skills: ["Forklift Operation", "Inventory Management", "Team Collaboration", "Health & Safety"],
        certifications: ["Forklift License", "Health & Safety Level 2", "Manual Handling"],
        work_experience: [
          {
            title: "Warehouse Operative",
            company: "Logistics Company in East London",
            period: "2019-2022",
            description: "Managed inventory, operated forklifts, ensured warehouse safety compliance"
          }
        ],
        summary: "Dedicated warehouse professional with proven track record in logistics operations. Strong work ethic and commitment to safety. Seeking to contribute to a growing team where reliability and attention to detail are valued.",
        ready_to_work: true,
        endorsements: ["Completed employability program with distinction", "Praised for punctuality and teamwork by job coach"],
        industry_interests: ["Logistics", "Manufacturing"],
        availability: "Full-time"
      },
      {
        id: "2",
        candidate_number: "11845",
        headline: "Customer-focused hospitality professional with Food Hygiene Level 2 certification.",
        skills: ["Customer Service", "Food Preparation", "Cash Handling", "Teamwork"],
        certifications: ["Food Hygiene Level 2", "First Aid at Work"],
        work_experience: [
          {
            title: "Kitchen Assistant",
            company: "Restaurant in Central London",
            period: "2020-2021",
            description: "Assisted with food preparation, maintained kitchen cleanliness, supported front-of-house during busy periods"
          }
        ],
        summary: "Passionate about hospitality with a genuine love for creating positive customer experiences. Reliable, energetic, and eager to develop skills in a supportive restaurant or café environment.",
        ready_to_work: true,
        endorsements: ["Excellent attendance record during training", "Demonstrated strong customer service skills"],
        industry_interests: ["Hospitality", "Retail"],
        availability: "Part-time"
      },
      {
        id: "3",
        candidate_number: "12003",
        headline: "Skilled construction worker with CSCS card and carpentry experience.",
        skills: ["Carpentry", "Site Safety", "Blueprint Reading", "Power Tools"],
        certifications: ["CSCS Card", "Construction Safety Certification", "Carpentry NVQ Level 2"],
        work_experience: [
          {
            title: "Carpenter's Mate",
            company: "Construction Site in South London",
            period: "2018-2020",
            description: "Assisted lead carpenter with residential and commercial projects, maintained tools and equipment"
          }
        ],
        summary: "Experienced in residential carpentry with strong foundation in construction safety. Looking for an opportunity to apply my skills and continue growing in the trades.",
        ready_to_work: true,
        endorsements: ["Completed trade skills program", "Strong practical skills noted by instructor"],
        industry_interests: ["Construction"],
        availability: "Immediate Start"
      }
    ];

    setCandidates(mockCandidates);

    // If viewing a specific candidate
    if (id) {
      const candidate = mockCandidates.find(c => c.id === id);
      if (candidate) {
        setSelectedCandidate(candidate);
      }
    }
  }, [id]);

  const handleSearch = () => {
    // In production, this would filter from Supabase
    let filtered = [...candidates];
    
    if (searchQuery) {
      filtered = filtered.filter(c => 
        c.headline.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.skills.some(s => s.toLowerCase().includes(searchQuery.toLowerCase())) ||
        c.certifications.some(cert => cert.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (selectedIndustries.length > 0) {
      filtered = filtered.filter(c => 
        c.industry_interests.some(i => selectedIndustries.includes(i))
      );
    }

    if (selectedAvailability.length > 0) {
      filtered = filtered.filter(c => 
        selectedAvailability.includes(c.availability)
      );
    }

    setCandidates(filtered);
  };

  const toggleIndustry = (industry: string) => {
    setSelectedIndustries(prev => 
      prev.includes(industry) 
        ? prev.filter(i => i !== industry)
        : [...prev, industry]
    );
  };

  const toggleAvailability = (availability: string) => {
    setSelectedAvailability(prev => 
      prev.includes(availability)
        ? prev.filter(a => a !== availability)
        : [...prev, availability]
    );
  };

  const handleViewProfile = (candidateId: string) => {
    navigate(`/employer/find-talent/${candidateId}`);
  };

  const handleRequestIntroduction = async () => {
    if (!selectedCandidate) return;
    
    // In production, this would create a connection request in Supabase
    toast.success("Introduction request sent! We'll connect you with this candidate soon.");
    navigate("/employer/dashboard");
  };

  const handleSaveCandidate = (candidateId: string) => {
    setSavedCandidates(prev => 
      prev.includes(candidateId)
        ? prev.filter(id => id !== candidateId)
        : [...prev, candidateId]
    );
    toast.success(
      savedCandidates.includes(candidateId) 
        ? "Candidate removed from saved list" 
        : "Candidate saved to your shortlist"
    );
  };

  // Detailed Profile View
  if (selectedCandidate) {
    return (
      <div className="min-h-screen bg-white">
        <header className="border-b">
          <div className="container py-6">
            <Button variant="ghost" onClick={() => {
              setSelectedCandidate(null);
              navigate("/employer/find-talent");
            }}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Search
            </Button>
          </div>
        </header>

        <div className="container py-8 max-w-4xl">
          <div className="mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Candidate #{selectedCandidate.candidate_number}</p>
                <h1 className="text-3xl font-bold mb-2">{selectedCandidate.headline}</h1>
                {selectedCandidate.ready_to_work && (
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                    <CheckCircle className="mr-1 h-3 w-3" />
                    Ready to Work
                  </Badge>
                )}
              </div>
              <Button
                variant="outline"
                onClick={() => handleSaveCandidate(selectedCandidate.id)}
              >
                <Bookmark className={`mr-2 h-4 w-4 ${savedCandidates.includes(selectedCandidate.id) ? "fill-current" : ""}`} />
                {savedCandidates.includes(selectedCandidate.id) ? "Saved" : "Save Candidate"}
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {selectedCandidate.skills.slice(0, 4).map((skill) => (
                <Badge key={skill} variant="secondary">{skill}</Badge>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {/* Candidate Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Candidate Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground leading-relaxed">{selectedCandidate.summary}</p>
              </CardContent>
            </Card>

            {/* Key Skills */}
            <Card>
              <CardHeader>
                <CardTitle>Key Skills</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {selectedCandidate.skills.map((skill) => (
                    <Badge key={skill} variant="outline">{skill}</Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Training & Certifications */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Training & Certifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {selectedCandidate.certifications.map((cert) => (
                    <div key={cert} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span>{cert}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Work Experience */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-5 w-5" />
                  Work Experience
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {selectedCandidate.work_experience.map((exp, index) => (
                    <div key={index}>
                      {index > 0 && <Separator className="my-4" />}
                      <h4 className="font-semibold">{exp.title}</h4>
                      <p className="text-sm text-muted-foreground">{exp.company}</p>
                      <p className="text-sm text-muted-foreground mb-2">{exp.period}</p>
                      <p className="text-muted-foreground">{exp.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Endorsements */}
            {selectedCandidate.endorsements.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Endorsements</CardTitle>
                  <CardDescription>Feedback from job coaches and training providers</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedCandidate.endorsements.map((endorsement, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <Heart className="h-4 w-4 text-red-500 mt-1" />
                        <p className="text-muted-foreground italic">{endorsement}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-4 mt-8">
            <Button 
              size="lg" 
              className="bg-[hsl(var(--employer-teal))] hover:bg-[hsl(var(--employer-teal))]/90 flex-1"
              onClick={handleRequestIntroduction}
            >
              Request Introduction
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => handleSaveCandidate(selectedCandidate.id)}
            >
              <Bookmark className={`mr-2 h-4 w-4 ${savedCandidates.includes(selectedCandidate.id) ? "fill-current" : ""}`} />
              {savedCandidates.includes(selectedCandidate.id) ? "Saved" : "Save"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Search & Browse View
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b">
        <div className="container py-6">
          <Button variant="ghost" onClick={() => navigate("/employer/dashboard")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[hsl(var(--employer-navy))] to-[hsl(var(--employer-navy))]/80 text-white py-16">
        <div className="container">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Meet London's Ready-to-Work Talent
          </h1>
          <p className="text-lg text-white/90 max-w-3xl">
            Proactively search our database of skilled and motivated candidates who have been 
            pre-vetted and are ready for a new opportunity.
          </p>
        </div>
      </section>

      <div className="container py-8">
        <div className="flex gap-8">
          {/* Filters Sidebar */}
          <aside className="w-64 flex-shrink-0">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="text-lg">Filters</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Industry Interests */}
                <div>
                  <Label className="font-semibold mb-3 block">Industry Interests</Label>
                  <div className="space-y-2">
                    {industries.map((industry) => (
                      <div key={industry} className="flex items-center space-x-2">
                        <Checkbox
                          id={industry}
                          checked={selectedIndustries.includes(industry)}
                          onCheckedChange={() => toggleIndustry(industry)}
                        />
                        <label htmlFor={industry} className="text-sm cursor-pointer">
                          {industry}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <Separator />

                {/* Availability */}
                <div>
                  <Label className="font-semibold mb-3 block">Availability</Label>
                  <div className="space-y-2">
                    {availabilityOptions.map((availability) => (
                      <div key={availability} className="flex items-center space-x-2">
                        <Checkbox
                          id={availability}
                          checked={selectedAvailability.includes(availability)}
                          onCheckedChange={() => toggleAvailability(availability)}
                        />
                        <label htmlFor={availability} className="text-sm cursor-pointer">
                          {availability}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <Button className="w-full" onClick={handleSearch}>
                  Apply Filters
                </Button>
              </CardContent>
            </Card>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by skill, certification, or keyword..."
                    className="pl-10"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  />
                </div>
                <Button onClick={handleSearch}>
                  <Search className="mr-2 h-4 w-4" />
                  Search
                </Button>
              </div>
            </div>

            {/* Results */}
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Showing {candidates.length} candidate{candidates.length !== 1 ? "s" : ""}
              </p>

              {candidates.map((candidate) => (
                <Card key={candidate.id} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CardTitle className="text-base">Candidate #{candidate.candidate_number}</CardTitle>
                          {candidate.ready_to_work && (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                              <CheckCircle className="mr-1 h-3 w-3" />
                              Ready to Work
                            </Badge>
                          )}
                        </div>
                        <CardDescription className="text-base font-medium text-foreground">
                          {candidate.headline}
                        </CardDescription>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleSaveCandidate(candidate.id)}
                      >
                        <Bookmark className={`h-4 w-4 ${savedCandidates.includes(candidate.id) ? "fill-current" : ""}`} />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-semibold mb-2">Key Skills:</p>
                        <div className="flex flex-wrap gap-2">
                          {candidate.skills.slice(0, 4).map((skill) => (
                            <Badge key={skill} variant="secondary">{skill}</Badge>
                          ))}
                        </div>
                      </div>

                      {candidate.certifications.length > 0 && (
                        <div>
                          <p className="text-sm font-semibold mb-2">Certifications:</p>
                          <div className="flex flex-wrap gap-2">
                            {candidate.certifications.slice(0, 3).map((cert) => (
                              <Badge key={cert} variant="outline" className="text-xs">
                                <Award className="mr-1 h-3 w-3" />
                                {cert}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <Button 
                        className="w-full mt-4"
                        onClick={() => handleViewProfile(candidate.id)}
                      >
                        View Profile
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {candidates.length === 0 && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <p className="text-muted-foreground">
                      No candidates found matching your criteria. Try adjusting your filters.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
